import React, { FC, ReactNode, useRef, useState } from 'react';
import type {
  OrganisationCardPayment,
  OrganisationCardPaymentDocument,
  OrganisationCardPaymentDocumentsResponse,
  OrganisationCardPaymentsResponse,
  OrganisationMember,
} from '@dreamai/types';
import { formatDate } from './organisationLabels';
import { openCheckoutPopup } from '../../utils/hostedCheckout';
import {
  CARD_STATUS_LABELS,
  OPEN_CARD_STATUSES,
  formatMoney,
} from './organisationCardLabels';

/** Calendar month as `YYYY-MM` (UTC). */
export type MonthKey = string;

export interface PurchaseLedgerQuery {
  month: MonthKey;
  membershipId: string | null;
}

export function currentMonthKey(now: Date = new Date()): MonthKey {
  return monthKey(now.getUTCFullYear(), now.getUTCMonth());
}

function monthKey(year: number, monthIndex: number): MonthKey {
  const d = new Date(Date.UTC(year, monthIndex, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthStart(key: MonthKey): Date {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1));
}

/** `[from, to)` for a calendar month, as ISO strings for the ledger query. */
export function monthWindow(key: MonthKey): { from: string; to: string } {
  const from = monthStart(key);
  const to = new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1),
  );
  return { from: from.toISOString(), to: to.toISOString() };
}

export function monthLabel(key: MonthKey): string {
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(monthStart(key));
}

/** The current month and the eleven before it, newest first. */
export function monthOptions(now: Date = new Date()): MonthKey[] {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  return Array.from({ length: 12 }, (_, i) => monthKey(year, month - i));
}

export function purchaseLedgerPath(
  base: string,
  query: PurchaseLedgerQuery,
): string {
  const { from, to } = monthWindow(query.month);
  const params = new URLSearchParams({ from, to });
  if (query.membershipId) params.set('membershipId', query.membershipId);
  return `${base}/card/payments?${params.toString()}`;
}

/**
 * What Dream's own checkout read off the shop's confirmation page. Local
 * mirror until `@dreamai/types` ships the fields.
 */
const CheckoutProof: FC<{ payment: OrganisationCardPayment }> = ({
  payment,
}) => {
  if (!payment.orderNumber && !payment.proofUrl) return null;
  return (
    <span className='organisation-purchase-proof'>
      {payment.orderNumber && <span>Order {payment.orderNumber}</span>}
      {payment.proofUrl && (
        <a
          className='btn-link'
          href={payment.proofUrl}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={`Proof for ${payment.purpose}`}
        >
          Proof
        </a>
      )}
    </span>
  );
};

export const PaymentRow: FC<{
  payment: OrganisationCardPayment;
  showMember: boolean;
  documents?: ReactNode;
}> = ({ payment, showMember, documents }) => {
  const open = OPEN_CARD_STATUSES.has(payment.status);
  return (
    <tr
      data-testid={`card-payment-${payment.id}`}
      className={open ? 'organisation-payment--open' : undefined}
    >
      <td>{formatDate(payment.createdAt)}</td>
      {showMember && <td>{payment.memberName ?? '—'}</td>}
      <td>
        {payment.purpose}
        <CheckoutProof payment={payment} />
      </td>
      <td>{formatMoney(payment.quotedAmount, payment.currency)}</td>
      <td>{formatMoney(payment.spentAmount, payment.currency)}</td>
      <td>{CARD_STATUS_LABELS[payment.status]}</td>
      {documents !== undefined && <td>{documents}</td>}
    </tr>
  );
};

type DocumentsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; href: string | null }
  | { kind: 'error' };

/**
 * The invoice's hosted page (invoice, credits applied, refunds issued, the
 * credit notes, PDF downloads); the PDF only as a fallback when there is no
 * hosted page.
 */
function invoicePageUrl(
  documents: OrganisationCardPaymentDocument[],
): string | null {
  const invoice = documents.find((d) => d.kind === 'invoice');
  const fallback = documents.find((d) => d.hostedUrl || d.pdfUrl);
  return (
    invoice?.hostedUrl ??
    invoice?.pdfUrl ??
    fallback?.hostedUrl ??
    fallback?.pdfUrl ??
    null
  );
}

/**
 * "View" on a finished purchase opens the invoice's hosted page. Airwallex
 * links expire, so the URL is fetched when the admin asks for it rather than
 * listed with the ledger; the tab is pre-opened so popup blockers allow it.
 */
const PurchaseDocuments: FC<{
  payment: OrganisationCardPayment;
  fetchDocuments: (
    paymentId: string,
  ) => Promise<OrganisationCardPaymentDocumentsResponse>;
}> = ({ payment, fetchDocuments }) => {
  const [state, setState] = useState<DocumentsState>({ kind: 'idle' });
  if (!payment.documentsAvailable) return <>—</>;

  const load = async () => {
    const tab = openCheckoutPopup();
    if (tab) tab.opener = null;
    setState({ kind: 'loading' });
    try {
      const { documents } = await fetchDocuments(payment.id);
      const href = invoicePageUrl(documents);
      setState({ kind: 'loaded', href });
      if (!href) tab?.close();
      else if (tab) tab.location.replace(href);
      else window.location.assign(href);
    } catch {
      tab?.close();
      setState({ kind: 'error' });
    }
  };

  if (state.kind === 'loading') {
    return <span className='organisation-purchase-documents'>Loading…</span>;
  }
  if (state.kind === 'loaded') {
    if (!state.href) return <>—</>;
    return (
      <span className='organisation-purchase-documents'>
        <a
          href={state.href}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={`Invoice for ${payment.purpose}`}
        >
          View
        </a>
      </span>
    );
  }
  return (
    <button
      type='button'
      className='btn-link'
      onClick={load}
      aria-label={`${state.kind === 'error' ? 'Retry loading' : 'Show'} documents for ${payment.purpose}`}
    >
      {state.kind === 'error' ? 'Try again' : 'View'}
    </button>
  );
};

interface OrganisationCardPaymentsProps {
  /** Current month, all members — what the panel loads up front. */
  ledger: OrganisationCardPaymentsResponse;
  currency: string;
  members: OrganisationMember[];
  fetchLedger: (
    query: PurchaseLedgerQuery,
  ) => Promise<OrganisationCardPaymentsResponse>;
  fetchDocuments: (
    paymentId: string,
  ) => Promise<OrganisationCardPaymentDocumentsResponse>;
}

/**
 * Purchases paid for by the organisation, one calendar month at a time and
 * optionally one member at a time. "Spent" is what shops have taken (open
 * purchases included), "Pending" the approved money still held for purchases
 * that are not finished — whichever month they were approved in, so it does
 * not change with the month. Finished purchases link to their invoice page
 * (which lists the credit notes). Members' personal purchases are never
 * included.
 */
const OrganisationCardPayments: FC<OrganisationCardPaymentsProps> = ({
  ledger: initialLedger,
  currency,
  members,
  fetchLedger,
  fetchDocuments,
}) => {
  const thisMonth = currentMonthKey();
  const [query, setQuery] = useState<PurchaseLedgerQuery>({
    month: thisMonth,
    membershipId: null,
  });
  const [ledger, setLedger] = useState(initialLedger);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestSeq = useRef(0);

  const apply = async (next: PurchaseLedgerQuery) => {
    setQuery(next);
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(false);
    try {
      const data = await fetchLedger(next);
      if (seq !== requestSeq.current) return;
      setLedger(data);
    } catch {
      if (seq !== requestSeq.current) return;
      setError(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const memberOptions = members
    .filter((m) => m.status === 'active')
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedMember = memberOptions.find((m) => m.id === query.membershipId);
  const isThisMonth = query.month === thisMonth;

  const emptyText =
    isThisMonth && !query.membershipId
      ? 'No purchases this month yet.'
      : `No purchases${selectedMember ? ` by ${selectedMember.name}` : ''} in ${monthLabel(query.month)}.`;

  return (
    <div className='organisation-card' data-testid='organisation-card-payments'>
      <div className='organisation-purchases-head'>
        <h3 className='organisation-card-title'>Purchases</h3>
        <div className='organisation-purchases-filters'>
          <select
            className='field-select compact'
            aria-label='Month'
            value={query.month}
            disabled={loading}
            onChange={(e) => apply({ ...query, month: e.target.value })}
          >
            {monthOptions().map((key) => (
              <option key={key} value={key}>
                {monthLabel(key)}
              </option>
            ))}
          </select>
          <select
            className='field-select compact'
            aria-label='Member'
            value={query.membershipId ?? ''}
            disabled={loading}
            onChange={(e) =>
              apply({ ...query, membershipId: e.target.value || null })
            }
          >
            <option value=''>All members</option>
            {memberOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!error && (
        <dl className='organisation-stats'>
          <div className='organisation-stat'>
            <dt>{isThisMonth ? 'Spent this month' : 'Spent'}</dt>
            <dd>{formatMoney(ledger.totalSpent, currency)}</dd>
          </div>
          <div className='organisation-stat'>
            <dt>Pending</dt>
            <dd>{formatMoney(ledger.totalPending, currency)}</dd>
          </div>
        </dl>
      )}

      {error ? (
        <p className='organisation-feedback error' role='alert'>
          Couldn&apos;t load purchases.{' '}
          <button
            type='button'
            className='btn-link'
            onClick={() => void apply(query)}
          >
            Try again
          </button>
        </p>
      ) : ledger.payments.length === 0 ? (
        <p className='organisation-empty'>{emptyText}</p>
      ) : (
        <div className='organisation-table-wrap'>
          <table
            className='organisation-table'
            aria-busy={loading || undefined}
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Member</th>
                <th>Purchase</th>
                <th>Approved</th>
                <th>Spent</th>
                <th>Status</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {ledger.payments.map((p) => (
                <PaymentRow
                  key={p.id}
                  payment={p}
                  showMember
                  documents={
                    <PurchaseDocuments
                      payment={p}
                      fetchDocuments={fetchDocuments}
                    />
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrganisationCardPayments;
